import { styled, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useEffect, useMemo, useRef, useState } from 'react';

import { GLOBAL_API_ERROR_CODES } from '@data/global-api-errors';
import type { GlobalApiErrorCode } from '@data/global-api-errors';
import { getChallengeData } from '@data/global-api-payments';
import {
  LiveRegionAnnouncer,
  useTranslationsContext,
} from '@state/checkout-contexts';
import type { ChkInputGroupProps } from '@ui/checkout-components';
import { ChkInputGroup } from '@ui/checkout-components';
import { UiButtonWithLoading } from '@ui/molecules-button-with-loading';
import { captureException, isNetworkError } from '@utility/checkout-utils';

import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import { HeaderWithCloseButton } from '../../components/HeaderWithCloseButton/HeaderWithCloseButton';
import { PhotoOtpImage } from '../../components/PhotoOtpImage/PhotoOtpImage';
import { PhotoOtpImageFallback } from '../../components/PhotoOtpImageFallback/PhotoOtpImageFallback';
import { ViewContainer } from '../../components/ViewContainer/ViewContainer';
import { ERROR_CODES_IN_ALERT } from '../../global.constants';
import { useCancelModal } from '../../hooks/useCancelModal/useCancelModal';
import { SCA_EVENTS } from '../../models/globals/events.constants';
import { TEST_IDS } from '../../models/globals/testIds.constants';
import { useScaSubject } from '../../providers/ScaSubjectProvider/ScaSubjectProvider.hooks';
import { dataLayer } from '../../utils/dataLayer';
import { getApiErrorCodeAndMessageKey } from '../../utils/getApiErrorCodeAndMessageKey';
import { getTraceIdAndTimestamp } from '../../utils/getTraceIdAndTimestamp';
import { isErrorHandled } from '../../utils/isErrorHandled';
import { TRANSLATION_KEYS } from '../../utils/TranslationKeys';

import {
  EMPTY_CHALLANGE_DATA,
  HANDLED_ERRORS,
  VERIFICATION_VIEW_CANCELLATION_REASONS,
  VERIFICATION_VIEW_CODE_DISABLED_REASON_ID,
  VERIFICATION_VIEW_NAME,
} from './VerificationView.constants';

export const VerificationView = () => {
  const [errorCode, setErrorCode] = useState<GlobalApiErrorCode | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslationsContext();

  const [code, setCode] = useState('');
  const theme = useTheme();
  const { updateSubject, isFetching, subject } = useScaSubject();
  const { modal, openModal } = useCancelModal({
    cancellationReasons: VERIFICATION_VIEW_CANCELLATION_REASONS,
    screenName: VERIFICATION_VIEW_NAME,
  });
  const [isInlineErrorVisible, setIsInlineErrorVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: photoOtpChallengeData, type: photoOtpChallengeType } = (() => {
    if (subject && 'accountAccessFlow' in subject) {
      return EMPTY_CHALLANGE_DATA;
    }
    return getChallengeData(subject) || EMPTY_CHALLANGE_DATA;
  })();

  const shouldDisplayPhotoOtpImage = Boolean(
    photoOtpChallengeData && photoOtpChallengeType
  );

  const helpTextKeyForInput = useMemo(
    () =>
      shouldDisplayPhotoOtpImage
        ? TRANSLATION_KEYS.SCAN_CODE_WITH_YOUR_APP
        : TRANSLATION_KEYS.YOUR_CODE_IS_DISPLAYED_IN_PUSHTAN_APP,
    [shouldDisplayPhotoOtpImage]
  );

  const handleSubmitClick = async () => {
    dataLayer.trackEvent({
      name: SCA_EVENTS.PUSHED_CODE,
      properties: { scaCode: code },
    });
    const { error } = await updateSubject({
      sca: {
        code,
      },
    });

    if (error !== null) {
      return handleError(error);
    }
  };

  const handlePhotoOtpImageLoad = () => {
    dataLayer.trackEvent({
      name: SCA_EVENTS.PHOTO_OTP_DISPLAYED,
      properties: {
        imageData: photoOtpChallengeData,
        imageType: photoOtpChallengeType,
      },
    });
  };

  const handlePhotoOtpImageLoadFailed = () => {
    dataLayer.trackEvent({
      name: SCA_EVENTS.PHOTO_OTP_FAILED_TO_DISPLAY,
      properties: {
        imageData: photoOtpChallengeData,
        imageType: photoOtpChallengeType,
      },
    });
  };

  useEffect(() => {
    dataLayer.trackEvent({ name: SCA_EVENTS.VERIFICATION_PAGE_DISPLAYED });
  }, []);

  const handleError = (error: unknown) => {
    const { code, messageKey, rawMessage } =
      getApiErrorCodeAndMessageKey(error);
    setErrorCode(code as GlobalApiErrorCode);
    setErrorMessage(
      messageKey && rawMessage
        ? `${t(messageKey)}${getTraceIdAndTimestamp(rawMessage)}`
        : null
    );

    if (code === GLOBAL_API_ERROR_CODES.CODE_424.UNAUTHORISED) {
      setIsInlineErrorVisible(true);
      inputRef.current?.focus();
    }

    dataLayer.trackEvent({
      name: SCA_EVENTS.VERIFICATION_PAGE_ERROR,
      properties: { code },
    });

    if (!isNetworkError(error) && !isErrorHandled(code, HANDLED_ERRORS)) {
      captureException(error);
    }
  };

  const codeInputProps: ChkInputGroupProps = useMemo(
    () => ({
      label: t(TRANSLATION_KEYS.VERIFICATION_CODE),
      inputType: 'text',
      testId: TEST_IDS.SCA_CODE_INPUT,
      value: code,
      onChange: (newValue: string) => setCode(newValue),
      fullWidth: true,
      helpText: (
        <Typography variant="body3" color={theme.coreTokens.text.muted}>
          {t(helpTextKeyForInput)}
        </Typography>
      ),
      helpTextId: TEST_IDS.CODE_INPUT_HELP_ID,
      error: isInlineErrorVisible,
      errorText: (
        <Typography variant="body3" color={theme.coreTokens.states.error}>
          {t(TRANSLATION_KEYS.UNAUTHORISED_VERIFICATION)}
        </Typography>
      ),
      errorTextId: TEST_IDS.CODE_INPUT_ERROR_ID,
      inputRef,
    }),
    [
      t,
      code,
      theme.coreTokens.text.muted,
      theme.coreTokens.states.error,
      helpTextKeyForInput,
      isInlineErrorVisible,
    ]
  );

  const isAlertVisible = Boolean(
    errorCode &&
    ERROR_CODES_IN_ALERT.includes(errorCode as GlobalApiErrorCode) &&
    errorMessage
  );

  const liveRegionSubmitMessage = isFetching
    ? t(TRANSLATION_KEYS.SUBMITTING_VERIFICATION_CODE)
    : null;

  return (
    <ViewContainer>
      <HeaderSection>
        <HeaderWithCloseButton
          onClick={openModal}
          aria-label={t(TRANSLATION_KEYS.OPEN_CANCEL_MODAL)}
          aria-haspopup={true}
        >
          {t(TRANSLATION_KEYS.VERIFY_PAYMENT)}
        </HeaderWithCloseButton>
      </HeaderSection>
      <MainSection>
        {shouldDisplayPhotoOtpImage && (
          <PhotoOtpImage
            data={photoOtpChallengeData}
            type={photoOtpChallengeType}
            alt={t(TRANSLATION_KEYS.PHOTO_OTP_CHALLENGE_IMAGE_DESCRIPTION)}
            onLoad={handlePhotoOtpImageLoad}
            onError={handlePhotoOtpImageLoadFailed}
            errorComponent={<PhotoOtpImageFallback />}
          />
        )}

        <StyledBox>
          <ChkInputGroup {...codeInputProps} />
        </StyledBox>

        <LiveRegionAnnouncer
          debounceDelay={500}
          message={liveRegionSubmitMessage}
        />
        <StyledButton
          variant="contained"
          onClick={handleSubmitClick}
          isLoading={isFetching}
          aria-disabled={!code}
          data-testid={TEST_IDS.SCA_VERIFICATION_VIEW_CTA}
          aria-describedby={
            !code ? VERIFICATION_VIEW_CODE_DISABLED_REASON_ID : undefined
          }
        >
          {t(TRANSLATION_KEYS.VERIFY)}
        </StyledButton>
        <span
          style={visuallyHidden}
          id={VERIFICATION_VIEW_CODE_DISABLED_REASON_ID}
        >
          {t(TRANSLATION_KEYS.PROVIDE_VALID_VERIFICATION_CODE_TO_CONTINUE)}
        </span>
        <ErrorAlert isVisible={isAlertVisible} message={errorMessage} />
      </MainSection>
      {modal}
    </ViewContainer>
  );
};

const StyledButton = styled(UiButtonWithLoading)(({ theme }) => ({
  marginTop: theme.spacing(9),
}));

const StyledBox = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(6),
}));

const MainSection = styled('main')({ display: 'contents' });

const HeaderSection = styled('header')({ display: 'contents' });
